import React, { useState, useEffect, useMemo } from 'react';
import { collection, getDocs, query } from '@firebase/firestore';
import { Row, Col, Card, Spinner, Form } from 'react-bootstrap';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger';

const logger = createLogger('ProductsDashboard');

function computeStats(products) {
  const now = new Date();
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  let totalProducts = products.length;
  let activeProducts = 0;
  let inactiveProducts = 0;
  let draftProducts = 0;
  let archivedProducts = 0;
  let productsWithoutImages = 0;
  let productsWithoutUpc = 0;
  let productsWithoutSeo = 0;
  let recentProducts = 0;
  const categoryBreakdown = {};

  products.forEach((product) => {
    const status = product.status || 'draft';
    if (status === 'active') {
      activeProducts++;
    } else if (status === 'draft') {
      draftProducts++;
      inactiveProducts++;
    } else if (status === 'archived') {
      archivedProducts++;
      inactiveProducts++;
    } else {
      inactiveProducts++;
    }

    if (!product.media || !Array.isArray(product.media) || product.media.length === 0) {
      productsWithoutImages++;
    }

    if (!product.upc || !String(product.upc).trim()) {
      productsWithoutUpc++;
    }

    if (!product.seo_name || !String(product.seo_name).trim()) {
      productsWithoutSeo++;
    }

    if (product.created_at) {
      let createdDate;
      if (product.created_at.toDate) {
        createdDate = product.created_at.toDate();
      } else if (product.created_at.seconds) {
        createdDate = new Date(product.created_at.seconds * 1000);
      } else if (typeof product.created_at === 'string') {
        createdDate = new Date(product.created_at);
      } else if (product.created_at instanceof Date) {
        createdDate = product.created_at;
      } else {
        createdDate = new Date(product.created_at);
      }
      if (createdDate && !isNaN(createdDate.getTime()) && createdDate >= sevenDaysAgo) {
        recentProducts++;
      }
    }

    const category = product.category || 'Uncategorized';
    categoryBreakdown[category] = (categoryBreakdown[category] || 0) + 1;
  });

  return {
    totalProducts,
    activeProducts,
    inactiveProducts,
    draftProducts,
    archivedProducts,
    productsWithoutImages,
    productsWithoutUpc,
    productsWithoutSeo,
    recentProducts,
    categoriesCount: Object.keys(categoryBreakdown).length,
    categoryBreakdown
  };
}

const ProductsDashboard = () => {
  const [allProducts, setAllProducts] = useState([]);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStatistics = async () => {
      setLoading(true);
      setError('');
      try {
        const productsQuery = query(collection(db, 'Products'));
        const querySnapshot = await getDocs(productsQuery);
        const fetched = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setAllProducts(fetched);
      } catch (err) {
        logger.error('[ProductsDashboard] Error fetching statistics:', err);
        setError('Failed to load product statistics. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  // Unique categories for dropdown (from all products)
  const categories = useMemo(() => {
    const set = new Set();
    allProducts.forEach(p => set.add(p.category || 'Uncategorized'));
    return ['', ...Array.from(set).sort((a, b) => (a === 'Uncategorized' ? 1 : b === 'Uncategorized' ? -1 : a.localeCompare(b)))];
  }, [allProducts]);

  // Stats for display (filtered by category when selected)
  const stats = useMemo(() => {
    const filtered = !categoryFilter
      ? allProducts
      : allProducts.filter(p => (p.category || 'Uncategorized') === categoryFilter);
    return computeStats(filtered);
  }, [allProducts, categoryFilter]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" role="status" className="mb-2">
          <span className="visually-hidden">Loading statistics...</span>
        </Spinner>
        <p className="text-muted">Loading product statistics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning mb-4" role="alert">
        {error}
      </div>
    );
  }

  // Get top categories (top 5)
  const topCategories = Object.entries(stats.categoryBreakdown)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5);

  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
        <h2 className="mb-0">Products Dashboard</h2>
        {categories.length > 1 && (
          <Form.Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            aria-label="Filter by category"
            style={{ fontSize: '16px', width: 'auto', minWidth: '180px' }}
          >
            <option value="">All categories</option>
            {categories.filter(c => c !== '').map(cat => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </Form.Select>
        )}
      </div>
      {categoryFilter && (
        <p className="text-muted small mb-3">Showing statistics for category: <strong>{categoryFilter}</strong></p>
      )}
      <Row className="g-3 mb-4">
        {/* Total Products */}
        <Col xs={12} sm={6} md={4} lg={3}>
          <Card className="h-100 shadow-sm border-primary">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Total Products</Card.Title>
                  <Card.Text className="mb-0 display-6 fw-bold text-primary">{stats.totalProducts}</Card.Text>
                </div>
                <div className="text-primary fs-1 opacity-50">
                  <i className="fas fa-box"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Active Products */}
        <Col xs={12} sm={6} md={4} lg={3}>
          <Card className="h-100 shadow-sm border-success">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Active Products</Card.Title>
                  <Card.Text className="mb-0 display-6 fw-bold text-success">{stats.activeProducts}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">
                    {stats.totalProducts > 0 
                      ? `${Math.round((stats.activeProducts / stats.totalProducts) * 100)}% of total`
                      : '0%'}
                  </Card.Text>
                </div>
                <div className="text-success fs-1 opacity-50">
                  <i className="fas fa-check-circle"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Inactive Products */}
        <Col xs={12} sm={6} md={4} lg={3}>
          <Card className="h-100 shadow-sm border-warning">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Inactive Products</Card.Title>
                  <Card.Text className="mb-0 display-6 fw-bold text-warning">{stats.inactiveProducts}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">
                    {stats.draftProducts > 0 && `${stats.draftProducts} draft`}
                    {stats.draftProducts > 0 && stats.archivedProducts > 0 && ', '}
                    {stats.archivedProducts > 0 && `${stats.archivedProducts} archived`}
                  </Card.Text>
                </div>
                <div className="text-warning fs-1 opacity-50">
                  <i className="fas fa-pause-circle"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Recent Products */}
        <Col xs={12} sm={6} md={4} lg={3}>
          <Card className="h-100 shadow-sm border-info">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Recent (7 days)</Card.Title>
                  <Card.Text className="mb-0 display-6 fw-bold text-info">{stats.recentProducts}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">New products</Card.Text>
                </div>
                <div className="text-info fs-1 opacity-50">
                  <i className="fas fa-clock"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        {/* Products Without Images */}
        <Col xs={12} sm={6} md={4}>
          <Card className="h-100 shadow-sm border-secondary">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Without Images</Card.Title>
                  <Card.Text className="mb-0 display-5 fw-bold text-secondary">{stats.productsWithoutImages}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">
                    {stats.totalProducts > 0 
                      ? `${Math.round((stats.productsWithoutImages / stats.totalProducts) * 100)}% of total`
                      : '0%'}
                  </Card.Text>
                </div>
                <div className="text-secondary fs-1 opacity-50">
                  <i className="fas fa-image"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Products Without UPC */}
        <Col xs={12} sm={6} md={4}>
          <Card className="h-100 shadow-sm border-warning">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Without UPC</Card.Title>
                  <Card.Text className="mb-0 display-5 fw-bold text-warning">{stats.productsWithoutUpc}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">
                    {stats.totalProducts > 0
                      ? `${Math.round((stats.productsWithoutUpc / stats.totalProducts) * 100)}% of total`
                      : '0%'}
                  </Card.Text>
                </div>
                <div className="text-warning fs-1 opacity-50">
                  <i className="fas fa-barcode"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Products Without SEO */}
        <Col xs={12} sm={6} md={4}>
          <Card className="h-100 shadow-sm border-danger">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Without SEO Name</Card.Title>
                  <Card.Text className="mb-0 display-5 fw-bold text-danger">{stats.productsWithoutSeo}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">
                    {stats.totalProducts > 0
                      ? `${Math.round((stats.productsWithoutSeo / stats.totalProducts) * 100)}% of total`
                      : '0%'}
                  </Card.Text>
                </div>
                <div className="text-danger fs-1 opacity-50">
                  <i className="fas fa-link"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xs={12} sm={6} md={4}>
          <Card className="h-100 shadow-sm border-primary">
            <Card.Body>
              <div className="d-flex align-items-center">
                <div className="flex-grow-1">
                  <Card.Title className="mb-1 small text-muted text-uppercase">Categories</Card.Title>
                  <Card.Text className="mb-0 display-5 fw-bold text-primary">{stats.categoriesCount}</Card.Text>
                  <Card.Text className="mb-0 small text-muted">Total categories</Card.Text>
                </div>
                <div className="text-primary fs-1 opacity-50">
                  <i className="fas fa-tags"></i>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        {/* Top Categories */}
        <Col xs={12} md={4}>
          <Card className="h-100 shadow-sm">
            <Card.Body>
              <Card.Title className="mb-3 small text-muted text-uppercase">Top Categories</Card.Title>
              {topCategories.length > 0 ? (
                <div>
                  {topCategories.map(([category, count], index) => (
                    <div key={category} className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                      <span className="text-truncate me-2">
                        <span className="badge bg-secondary me-2">{index + 1}</span>
                        {category}
                      </span>
                      <span className="fw-bold">{count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-muted mb-0">No categories found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default ProductsDashboard;